import { useEffect, useState } from 'react';

import { useEndpoint } from '../../../../api/hooks/endpoint/useEndpoint';
import { Button } from '../../../common/button/Button';
import { Input } from '../../../common/input/Input';
import { Select } from '../../../common/select/Select';
import { ShopLocationPicker } from './ShopLocationPicker';

import './delivery-config-panel.scss';

const PRICING_MODE_OPTIONS = [
  { id: 'FLAT_FEE', value: 'FLAT_FEE', label: 'Flat Fee' },
  { id: 'BASE_PLUS_PER_KM', value: 'BASE_PLUS_PER_KM', label: 'Base Fee + Per KM' },
  { id: 'PER_KM', value: 'PER_KM', label: 'Per KM' }
];

const DEFAULT_FORM = {
  pricingMode: 'PER_KM',
  flatFee: '',
  pricePerKm: '',
  minFee: '',
  maxFee: '',
  originLat: null,
  originLng: null
};

export const DeliveryConfigPanel = () => {
  const deliveryConfigQuery = useEndpoint('/delivery/config', 'delivery-config');
  const [form, setForm] = useState(DEFAULT_FORM);
  const [saved, setSaved] = useState(false);

  useEffect(() => {
    if (deliveryConfigQuery.data) {
      const d = deliveryConfigQuery.data;
      setForm({
        pricingMode: d.pricingMode ?? 'PER_KM',
        flatFee: d.flatFee ?? '',
        pricePerKm: d.pricePerKm ?? '',
        minFee: d.minFee ?? '',
        maxFee: d.maxFee ?? '',
        originLat: d.originLat ?? null,
        originLng: d.originLng ?? null
      });
    }
  }, [deliveryConfigQuery.data]);

  const handleChange = ({ name, value }) => {
    setForm((prev) => ({ ...prev, [name]: value }));
    setSaved(false);
  };

  const handleModeChange = (e) => {
    setForm((prev) => ({ ...prev, pricingMode: e.target.value }));
    setSaved(false);
  };

  const handleLocationChange = ({ lat, lng }) => {
    setForm((prev) => ({ ...prev, originLat: lat, originLng: lng }));
    setSaved(false);
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    const payload = {
      pricingMode: form.pricingMode,
      flatFee: form.flatFee !== '' ? parseFloat(form.flatFee) : null,
      pricePerKm: form.pricePerKm !== '' ? parseFloat(form.pricePerKm) : null,
      minFee: form.minFee !== '' ? parseFloat(form.minFee) : null,
      maxFee: form.maxFee !== '' ? parseFloat(form.maxFee) : null,
      originLat: form.originLat,
      originLng: form.originLng
    };

    const isNew = !deliveryConfigQuery.data?.id;
    const action = isNew ? deliveryConfigQuery.create : deliveryConfigQuery.update;

    action({
      data: payload,
      onSuccess: () => setSaved(true),
      onError: () => setSaved(false)
    });
  };

  const showFlatFee = form.pricingMode === 'FLAT_FEE' || form.pricingMode === 'BASE_PLUS_PER_KM';
  const showPerKm = form.pricingMode === 'BASE_PLUS_PER_KM' || form.pricingMode === 'PER_KM';
  const flatFeeLabel = form.pricingMode === 'FLAT_FEE' ? 'Flat Fee (R)' : 'Base Fee (R)';

  if (deliveryConfigQuery.loading) return <p className="delivery-config-panel__loading">Loading...</p>;

  return (
    <form className="delivery-config-panel" onSubmit={handleSubmit}>

      <div className="delivery-config-card">
        <div className="delivery-config-card__header">
          <h3>Shop Location</h3>
        </div>
        <div className="delivery-config-card__body">
          <ShopLocationPicker
            lat={form.originLat}
            lng={form.originLng}
            onChange={handleLocationChange}
          />
        </div>
      </div>

      <div className="delivery-config-card">
        <div className="delivery-config-card__header">
          <h3>Pricing Configuration</h3>
        </div>
        <div className="delivery-config-card__body">
          <div className="delivery-config-panel__select">
            <Select
              label="Pricing Mode"
              showLabel
              required
              value={form.pricingMode}
              options={PRICING_MODE_OPTIONS}
              onChange={handleModeChange}
              dropdownWidth={240}
              dropdownClassName="delivery-config-panel__dropdown"
            />
          </div>

          <div className="delivery-config-card__grid">
            {showFlatFee && (
              <Input
                label={flatFeeLabel}
                name="flatFee"
                type="number"
                value={String(form.flatFee)}
                onChange={handleChange}
                required
                props={{ min: 0, step: '0.01' }}
              />
            )}

            {showPerKm && (
              <Input
                label="Price Per KM (R)"
                name="pricePerKm"
                type="number"
                value={String(form.pricePerKm)}
                onChange={handleChange}
                required
                props={{ min: 0, step: '0.01' }}
              />
            )}

            <Input
              label="Minimum Fee (R)"
              name="minFee"
              type="number"
              value={String(form.minFee)}
              onChange={handleChange}
              placeholder="Optional"
              props={{ min: 0, step: '0.01' }}
            />

            <Input
              label="Maximum Fee (R)"
              name="maxFee"
              type="number"
              value={String(form.maxFee)}
              onChange={handleChange}
              placeholder="Optional"
              props={{ min: 0, step: '0.01' }}
            />
          </div>
        </div>

        <div className="delivery-config-card__footer">
          <Button type="submit" loading={deliveryConfigQuery.mutating} disabled={deliveryConfigQuery.mutating}>
            Save
          </Button>
          {saved && <span className="delivery-config-panel__saved">Saved</span>}
          {deliveryConfigQuery.mutatingError && (
            <span className="delivery-config-panel__error">Failed to save</span>
          )}
        </div>
      </div>

    </form>
  );
};
